import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaDatabase, FaArrowUp, FaArrowDown, FaSave, FaUndo, FaPlus, FaTimes, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import './TicketWorkflow.css';

// Enrichment settings (docs/data-sources-v2-spec.md §6, docs/ticket-journey.md):
// which data sources run, in what order, under which conditions, how the step
// ends and what happens when nothing is found, plus the extra moments the
// sources run again. Shown on the data sources page (/connectors?view=workflow);
// the steps of a ticket themselves are the journey above it.

const API = '/api/automation/ticket-workflow';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

const emptyConditions = () => ({ channels: null, clients: null, carriers: null, only_if_missing_order: false, requires: null });

const TicketWorkflow = ({ onFeedback }) => {
    const { t } = useTranslation();
    const [workflow, setWorkflow] = useState(null);
    const [options, setOptions] = useState(null);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [dirty, setDirty] = useState(false);

    const load = useCallback(async () => {
        setError(null);
        try {
            const [wf, opts] = await Promise.all([axios.get(API, authHeaders()), axios.get(`${API}/options`, authHeaders())]);
            setWorkflow(wf.data);
            setDraft(JSON.parse(JSON.stringify(wf.data.enrichment)));
            setOptions(opts.data);
            setDirty(false);
        } catch (e) {
            setError(t('ticketWorkflow.loadError', 'Impossible de charger le parcours du ticket.'));
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const update = (patch) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); };
    const updateSource = (index, patch) => update({ sources: draft.sources.map((s, i) => (i === index ? { ...s, ...patch } : s)) });
    const updateConditions = (index, patch) => updateSource(index, { conditions: { ...(draft.sources[index].conditions || emptyConditions()), ...patch } });
    const move = (index, delta) => {
        const next = [...draft.sources];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        update({ sources: next });
    };
    const removeSource = (index) => update({ sources: draft.sources.filter((_, i) => i !== index) });
    const addSource = (key) => { if (key && !draft.sources.some((s) => s.key === key)) update({ sources: [...draft.sources, { key, enabled: true, conditions: emptyConditions() }] }); };

    const save = async () => {
        setBusy('save');
        setError(null);
        try {
            const res = await axios.put(API, { enrichment: draft }, authHeaders());
            setWorkflow(res.data);
            setDraft(JSON.parse(JSON.stringify(res.data.enrichment)));
            setDirty(false);
            onFeedback?.({ type: 'success', message: t('ticketWorkflow.saved', 'Parcours enregistré.') });
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : t('ticketWorkflow.saveError', 'Enregistrement impossible.'));
        } finally {
            setBusy(null);
        }
    };

    const reset = async () => {
        setBusy('reset');
        try {
            await axios.delete(API, authHeaders());
            await load();
            onFeedback?.({ type: 'success', message: t('ticketWorkflow.resetDone', 'Parcours remis au comportement par défaut.') });
        } catch (e) {
            setError(t('ticketWorkflow.saveError', 'Enregistrement impossible.'));
        } finally {
            setBusy(null);
        }
    };

    const sourceTitles = useMemo(() => Object.fromEntries((options?.sources || []).map((s) => [s.key, s.title])), [options]);
    const unlisted = useMemo(() => (options?.sources || []).filter((s) => !draft?.sources.some((d) => d.key === s.key)), [options, draft]);

    if (!draft || !options) {
        return <div className="aa-card"><p className="tw-muted">{error || t('ticketWorkflow.loading', 'Chargement…')}</p></div>;
    }

    return (
        <div className="tw-page">
            {error && <div className="aa-feedback error" role="alert"><FaTimesCircle /> {error}</div>}
            <div className="tw-status">
                {workflow?.persisted
                    ? <span><FaCheckCircle className="tw-ok" /> {t('ticketWorkflow.persisted', 'Parcours personnalisé, modifié le {{at}}', { at: workflow.updated_at ? new Date(workflow.updated_at).toLocaleString() : '—' })}</span>
                    : <span>{t('ticketWorkflow.implicitV2', 'Réglage par défaut : toutes vos sources, dans l’ordre de leur création, la première valeur trouvée l’emporte. Enregistrez pour le personnaliser.')}</span>}
                <div className="tw-status-actions">
                    {workflow?.persisted && <button type="button" className="tw-btn" disabled={busy === 'reset'} onClick={reset}><FaUndo /> {t('ticketWorkflow.reset', 'Revenir au défaut')}</button>}
                    <button type="button" className="tw-btn tw-btn-primary" disabled={busy === 'save' || !dirty} onClick={save}><FaSave /> {t('ticketWorkflow.save', 'Enregistrer')}</button>
                </div>
            </div>

            <ol className="tw-steps">
                <li className="tw-step tw-step-active">
                    <div className="tw-step-icon"><FaDatabase /></div>
                    <div className="tw-step-body">
                        <h3>{t('ticketWorkflow.settingsTitle', 'Réglages de l’enrichissement')}</h3>
                        <p className="tw-muted">{t('ticketWorkflow.settingsDesc', 'Quelles sources lisent la commande, les colis et le suivi, dans quel ordre et sous quelles conditions. Le suivi des colis est en plus relu toutes les 12 h pendant 45 jours, indépendamment de ces réglages.')}</p>

                        <div className="tw-sources">
                            {draft.sources.length === 0 && <p className="tw-muted tw-empty">{t('ticketWorkflow.noSources', 'Aucune source dans cette étape : les tickets ne sont pas enrichis.')}</p>}
                            {draft.sources.map((source, index) => (
                                <SourceCard key={source.key} index={index} source={source} title={sourceTitles[source.key] || source.key}
                                    known={Boolean(sourceTitles[source.key])} options={options} t={t}
                                    onMove={move} onRemove={removeSource} onUpdate={updateSource} onConditions={updateConditions} total={draft.sources.length} />
                            ))}
                            {unlisted.length > 0 && (
                                <div className="tw-add">
                                    <FaPlus />
                                    <select className="tw-input" value="" onChange={(e) => addSource(e.target.value)}>
                                        <option value="">{t('ticketWorkflow.addSource', 'Ajouter une source à l’étape…')}</option>
                                        {unlisted.map((s) => <option key={s.key} value={s.key}>{s.title}</option>)}
                                    </select>
                                </div>
                            )}
                            {options.sources.length === 0 && <p className="tw-muted">{t('ticketWorkflow.noSourcesConfigured', 'Aucune source de données configurée : créez-en une sur la page Sources de données.')}</p>}
                        </div>

                        <div className="tw-settings">
                            <label className="tw-field">
                                <span>{t('ticketWorkflow.strategy', 'Comportement')}</span>
                                <select className="tw-input" value={draft.strategy} onChange={(e) => update({ strategy: e.target.value })}>
                                    <option value="first_found">{t('ticketWorkflow.strategy_first_found', 'S’arrêter à la première source qui trouve')}</option>
                                    <option value="merge">{t('ticketWorkflow.strategy_merge', 'Interroger toutes les sources et fusionner (la première valeur gagne)')}</option>
                                </select>
                            </label>
                            <label className="tw-field">
                                <span>{t('ticketWorkflow.timeout', 'Délai maximal de l’étape (s)')}</span>
                                <input className="tw-input tw-short" type="number" min={options.timeout_bounds[0]} max={options.timeout_bounds[1]} value={draft.timeout_s}
                                    onChange={(e) => update({ timeout_s: parseInt(e.target.value, 10) || options.timeout_bounds[0] })} />
                            </label>
                            <label className="tw-field">
                                <span>{t('ticketWorkflow.onNotFound', 'Si rien n’est trouvé')}</span>
                                <select className="tw-input" value={draft.on_not_found} onChange={(e) => update({ on_not_found: e.target.value })}>
                                    <option value="continue">{t('ticketWorkflow.onNotFound_continue', 'Continuer normalement')}</option>
                                    <option value="flag_missing_info">{t('ticketWorkflow.onNotFound_flag', 'Signaler le ticket à l’équipe (informations manquantes)')}</option>
                                    <option value="ask_customer">{t('ticketWorkflow.onNotFound_ask', 'Demander l’identifiant au client')}</option>
                                </select>
                            </label>
                            <label className="tw-check">
                                <input type="checkbox" checked={draft.include_unlisted !== false} onChange={(e) => update({ include_unlisted: e.target.checked })} />
                                {t('ticketWorkflow.includeUnlisted', 'Les sources créées plus tard rejoignent l’étape en fin de liste')}
                            </label>
                        </div>

                        <h4>{t('ticketWorkflow.alsoOn', 'Enrichir aussi quand…')}</h4>
                        <div className="tw-also">
                            <label className="tw-check">
                                <input type="checkbox" checked={Boolean(draft.also_on?.new_customer_message)} onChange={(e) => update({ also_on: { ...draft.also_on, new_customer_message: e.target.checked } })} />
                                {t('ticketWorkflow.also_newMessage', 'le client envoie un nouveau message')}
                            </label>
                            <label className="tw-check">
                                <input type="checkbox" checked={Boolean(draft.also_on?.agent_tool)} onChange={(e) => update({ also_on: { ...draft.also_on, agent_tool: e.target.checked } })} />
                                {t('ticketWorkflow.also_agentTool', 'l’agent le demande pendant sa réponse (outil)')}
                            </label>
                            <label className="tw-check">
                                <input type="checkbox" checked={Boolean(draft.also_on?.scheduled_every_minutes)} onChange={(e) => update({ also_on: { ...draft.also_on, scheduled_every_minutes: e.target.checked ? 360 : null } })} />
                                {t('ticketWorkflow.also_scheduled', 'toutes les')}
                                <input className="tw-input tw-short" type="number" min={options.min_schedule_minutes} disabled={!draft.also_on?.scheduled_every_minutes}
                                    value={draft.also_on?.scheduled_every_minutes || ''} onChange={(e) => update({ also_on: { ...draft.also_on, scheduled_every_minutes: parseInt(e.target.value, 10) || options.min_schedule_minutes } })} />
                                {t('ticketWorkflow.also_minutes', 'minutes pour les tickets ouverts')}
                            </label>
                        </div>
                    </div>
                </li>

            </ol>
        </div>
    );
};

const ChipSelect = ({ label, values, selected, onChange, t, labelOf }) => {
    const current = selected || [];
    const toggle = (value) => {
        const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
        onChange(next.length ? next : null);
    };
    return (
        <div className="tw-cond">
            <span className="tw-cond-label">{label} <em>{current.length ? '' : t('ticketWorkflow.all', '(tous)')}</em></span>
            <div className="tw-chips">
                {values.map((value) => (
                    <label key={value} className={`tw-chip ${current.includes(value) ? 'on' : ''}`}>
                        <input type="checkbox" checked={current.includes(value)} onChange={() => toggle(value)} /> {labelOf ? labelOf(value) : value}
                    </label>
                ))}
            </div>
        </div>
    );
};

const SourceCard = ({ index, total, source, title, known, options, t, onMove, onRemove, onUpdate, onConditions }) => {
    const conditions = source.conditions || emptyConditions();
    const channelLabel = (c) => t(`ticketWorkflow.channel.${c}`, c);
    return (
        <div className={`tw-source ${source.enabled === false ? 'off' : ''}`}>
            <div className="tw-source-head">
                <span className="tw-order">{index + 1}</span>
                <strong>{title}</strong>
                {!known && <span className="tw-pill tw-pill-warn">{t('ticketWorkflow.unknownSource', 'source introuvable')}</span>}
                <label className="tw-check tw-inline">
                    <input type="checkbox" checked={source.enabled !== false} onChange={(e) => onUpdate(index, { enabled: e.target.checked })} /> {t('ticketWorkflow.enabled', 'active')}
                </label>
                <div className="tw-source-actions">
                    <button type="button" className="tw-icon" disabled={index === 0} onClick={() => onMove(index, -1)} title={t('ticketWorkflow.moveUp', 'Monter')}><FaArrowUp /></button>
                    <button type="button" className="tw-icon" disabled={index === total - 1} onClick={() => onMove(index, 1)} title={t('ticketWorkflow.moveDown', 'Descendre')}><FaArrowDown /></button>
                    <button type="button" className="tw-icon" onClick={() => onRemove(index)} title={t('ticketWorkflow.remove', 'Retirer de l’étape')}><FaTimes /></button>
                </div>
            </div>
            <details className="tw-conditions">
                <summary>{t('ticketWorkflow.conditions', 'Conditions')} {summarizeConditions(conditions, t)}</summary>
                <ChipSelect label={t('ticketWorkflow.channels', 'Canaux')} values={options.channels} selected={conditions.channels} onChange={(v) => onConditions(index, { channels: v })} t={t} labelOf={channelLabel} />
                {options.clients.length > 0 && (
                    <ChipSelect label={t('ticketWorkflow.clients', 'Clients B2B')} values={options.clients.map((c) => c.name)} selected={conditions.clients} onChange={(v) => onConditions(index, { clients: v })} t={t} />
                )}
                {options.carriers.length > 0 && (
                    <ChipSelect label={t('ticketWorkflow.carriers', 'Transporteurs')} values={options.carriers} selected={conditions.carriers} onChange={(v) => onConditions(index, { carriers: v })} t={t} />
                )}
                <ChipSelect label={t('ticketWorkflow.requires', 'Seulement si le ticket porte')} values={options.identifiers} selected={conditions.requires} onChange={(v) => onConditions(index, { requires: v })} t={t}
                    labelOf={(v) => t(`ticketWorkflow.identifier.${v}`, v)} />
                <label className="tw-check">
                    <input type="checkbox" checked={Boolean(conditions.only_if_missing_order)} onChange={(e) => onConditions(index, { only_if_missing_order: e.target.checked })} />
                    {t('ticketWorkflow.onlyIfMissingOrder', 'Seulement si le ticket n’a pas encore de commande')}
                </label>
            </details>
        </div>
    );
};

const summarizeConditions = (conditions, t) => {
    const parts = [];
    if (conditions.channels?.length) parts.push(`${t('ticketWorkflow.channels', 'Canaux')} : ${conditions.channels.length}`);
    if (conditions.clients?.length) parts.push(`${t('ticketWorkflow.clients', 'Clients B2B')} : ${conditions.clients.length}`);
    if (conditions.carriers?.length) parts.push(`${t('ticketWorkflow.carriers', 'Transporteurs')} : ${conditions.carriers.length}`);
    if (conditions.requires?.length) parts.push(`${t('ticketWorkflow.requiresShort', 'identifiants')} : ${conditions.requires.join(', ')}`);
    if (conditions.only_if_missing_order) parts.push(t('ticketWorkflow.onlyIfMissingOrderShort', 'sans commande'));
    return <span className="tw-muted">{parts.length ? `— ${parts.join(' · ')}` : `— ${t('ticketWorkflow.always', 'toujours')}`}</span>;
};

export default TicketWorkflow;
